import { api } from './api'
import { WhatsAppMailbox, PaginatedResponse } from '@/types'

/**
 * WhatsApp Mailbox Service
 * QR Code Authentication (Baileys) API
 * Version: 2.0.0
 */

// ============================================================================
// Request/Response Types
// ============================================================================

export interface WhatsAppMailboxFilters {
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'RESTRICTED'
  connectionType?: 'BUSINESS_API' | 'BAILEYS'
  qualityRating?: 'GREEN' | 'YELLOW' | 'RED' | 'UNKNOWN'
  accountStatus?: 'CONNECTED' | 'DISCONNECTED' | 'FLAGGED' | 'RESTRICTED' | 'RATE_LIMITED' | 'PENDING' | 'BANNED'
  userId?: number
  search?: string
  isConnected?: boolean
  page?: number
  pageSize?: number
}

export interface CreateWhatsAppMailboxRequest {
  name: string
  phoneNumber: string
  displayName?: string
  businessDescription?: string
  connectionType: 'BUSINESS_API' | 'BAILEYS'
  webhookUrl?: string
  notes?: string
}

export interface UpdateWhatsAppMailboxRequest {
  name?: string
  displayName?: string
  businessDescription?: string
  connectionType?: 'BUSINESS_API' | 'BAILEYS'
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'RESTRICTED'
  webhookUrl?: string
  notes?: string
}

export interface WhatsAppMailboxStats {
  total: number
  active: number
  inactive: number
  suspended: number
  restricted: number
  availableCapacity: number // Set to 0 for QR code authentication
  todaysSent: number // Set to 0 for QR code authentication
  byProvider: {
    BUSINESS_API: number
    CLOUD_API: number // Used for BAILEYS connection type
  }
  byQualityRating: {
    GREEN: number
    YELLOW: number
    RED: number
    UNKNOWN: number
  }
  byAccountStatus: {
    CONNECTED: number
    DISCONNECTED: number
    FLAGGED: number
    RATE_LIMITED: number
    PENDING: number
    BANNED: number
  }
}

export interface WhatsAppMailboxHealth {
  id: number
  name: string
  phoneNumber: string
  qualityRating: string
  accountStatus: string
  isConnected: boolean
  lastHealthCheckAt: string | null
  healthScore: number
  issues: string[]
  recommendations: string[]
}

// ============================================================================
// API Service
// ============================================================================

export const whatsappMailboxService = {
  async fetchWhatsAppMailboxes(filters: WhatsAppMailboxFilters = {}): Promise<PaginatedResponse<WhatsAppMailbox>> {
    // Backend returns: { success: true, data: WhatsAppMailbox[], pagination: { total, page, pageSize } }
    const response = await api.get<{
      success: boolean
      data: WhatsAppMailbox[]
      pagination: {
        total: number
        page: number
        pageSize: number
      }
    }>('/whatsapp-mailboxes', {
      params: filters,
    })
    return {
      data: response.data.data || [],
      pagination: {
        page: response.data.pagination?.page || 1,
        limit: response.data.pagination?.pageSize || 10,
        total: response.data.pagination?.total || 0,
        totalPages: Math.ceil((response.data.pagination?.total || 0) / (response.data.pagination?.pageSize || 10)),
      },
    }
  },

  async getWhatsAppMailbox(id: number): Promise<WhatsAppMailbox> {
    // Backend returns: { success: true, data: WhatsAppMailbox }
    const response = await api.get<{ success: boolean; data: WhatsAppMailbox }>(`/whatsapp-mailboxes/${id}`)
    return response.data.data
  },

  async createWhatsAppMailbox(data: CreateWhatsAppMailboxRequest): Promise<WhatsAppMailbox> {
    // Backend returns: { success: true, data: WhatsAppMailbox }
    const response = await api.post<{ success: boolean; data: WhatsAppMailbox }>('/whatsapp-mailboxes', data)
    return response.data.data
  },

  async updateWhatsAppMailbox(id: number, data: UpdateWhatsAppMailboxRequest): Promise<WhatsAppMailbox> {
    // Backend returns: { success: true, data: WhatsAppMailbox }
    const response = await api.put<{ success: boolean; data: WhatsAppMailbox }>(`/whatsapp-mailboxes/${id}`, data)
    return response.data.data
  },

  async deleteWhatsAppMailbox(id: number): Promise<void> {
    await api.delete(`/whatsapp-mailboxes/${id}`)
  },

  async resetDailyMessageCount(id: number): Promise<void> {
    await api.post(`/whatsapp-mailboxes/${id}/reset-count`)
  },

  async getStats(): Promise<WhatsAppMailboxStats> {
    // Backend may return: { success: true, data: WhatsAppMailboxStats }
    const response = await api.get<{ success: boolean; data: WhatsAppMailboxStats }>('/whatsapp-mailboxes/stats')
    return response.data.data || (response.data as unknown as WhatsAppMailboxStats)
  },

  async getHealth(id: number): Promise<WhatsAppMailboxHealth> {
    // Backend returns: { success: true, data: WhatsAppMailboxHealth }
    const response = await api.get<{ success: boolean; data: WhatsAppMailboxHealth }>(`/whatsapp-mailboxes/${id}/health`)
    return response.data.data
  },

  async generateQRCode(id: number): Promise<{ qrCode: string; expiresIn: number }> {
    // Backend returns: { success: true, data: { qrCode: string, expiresIn: number }, message: string }
    const response = await api.post<{
      success: boolean
      data: {
        qrCode: string
        expiresIn: number
      }
      message: string
    }>(`/whatsapp-mailboxes/${id}/generate-qr`)
    return response.data.data
  },

  async disconnectMailbox(id: number): Promise<void> {
    // Backend returns: { success: true, message: string }
    await api.post(`/whatsapp-mailboxes/${id}/disconnect`)
  },
}
